import type { SvgIconComponent } from "@mui/icons-material";
import { createSvgIcon as createMuiSvgIcon } from "@mui/material";
import type { ReactNode } from "react";

import { SIM_TYPE } from "../../lib/logistics";
import { CrmIconTile } from "../crm/CrmObjectIcon";

// @mui/icons-material brings its own copy of MUI, so its icon type is not
// the type createSvgIcon returns; the component is the same.
const createSvgIcon = (path: ReactNode, name: string) => createMuiSvgIcon(path, name) as SvgIconComponent;

// Line icons on a 24 grid; the stroke follows the icon colour.
const stroked = (children: ReactNode) => (
  <g fill="none" stroke="currentColor" strokeWidth={1.75} strokeLinecap="round" strokeLinejoin="round">
    {children}
  </g>
);

const TabletIcon = createSvgIcon(
  stroked(
    <>
      <rect x="4.5" y="2.5" width="15" height="19" rx="2.25" />
      <path d="M10.5 18.5h3" />
    </>
  ),
  "InventoryTablet"
);

// The square box with its remote.
const TvBoxIcon = createSvgIcon(
  stroked(
    <>
      <rect x="2" y="2.5" width="5" height="19" rx="2.5" />
      <circle cx="4.5" cy="7.25" r="1.5" />
      <circle cx="4.5" cy="12" r="0.6" fill="currentColor" stroke="none" />
      <circle cx="4.5" cy="15" r="0.6" fill="currentColor" stroke="none" />
      <rect x="9.5" y="6" width="12.5" height="12.5" rx="2.5" />
      <path d="M13.5 12.25h4.5" />
    </>
  ),
  "InventoryTvBox"
);

const SimIcon = createSvgIcon(
  <>
    {stroked(<path d="M7 2.5h7.5L19 7v12.5a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2v-15a2 2 0 0 1 2-2z" />)}
    <g fill="none" stroke="currentColor" strokeWidth={1.25} strokeLinejoin="round">
      <rect x="8" y="10.5" width="8" height="7.5" rx="1.25" />
      <path d="M8 14.25h8M12 10.5v3.75" />
    </g>
  </>,
  "InventorySim"
);

// The Prowise touchscreen on its mobile stand.
const ProwiseIcon = createSvgIcon(
  <>
    {stroked(
      <>
        <rect x="2" y="2.5" width="20" height="12.5" rx="1.5" />
        <path d="M2 12.25h20M12 15v4.5M6 19.5h12" />
      </>
    )}
    <circle cx="7" cy="21.5" r="1.1" fill="currentColor" />
    <circle cx="17" cy="21.5" r="1.1" fill="currentColor" />
  </>,
  "InventoryProwise"
);

const PackageIcon = createSvgIcon(
  stroked(<path d="M12 2.75l8.25 4.5v9.5L12 21.25l-8.25-4.5v-9.5zM3.75 7.25L12 11.75l8.25-4.5M12 11.75v9.5" />),
  "InventoryPackage"
);

const TYPE_ICONS: Record<string, { Icon: SvgIconComponent; color: string }> = {
  Tablet: { Icon: TabletIcon, color: "#3a8fd9" },
  "TV Box": { Icon: TvBoxIcon, color: "#7c6bd6" },
  [SIM_TYPE]: { Icon: SimIcon, color: "#0c9d8a" },
  Aktivitätstisch: { Icon: ProwiseIcon, color: "#e9803b" },
};
const OTHER = { Icon: PackageIcon, color: "#6b7f99" };
const NO_TYPE = { Icon: PackageIcon, color: "#a8a6a3" };

const typeIcon = (type: string | null) => (type === null ? NO_TYPE : (TYPE_ICONS[type] ?? OTHER));

export const inventoryTypeColor = (type: string | null): string => typeIcon(type).color;

// The tile of an inventory type, in the style of the Salesforce object tiles.
// A type without its own icon, and a unit without a type, shows a package.
export const InventoryTypeIcon = ({ type, size = 32 }: { type: string | null; size?: number }) => {
  const { Icon, color } = typeIcon(type);
  return <CrmIconTile Icon={Icon} color={color} size={size} />;
};
